// frontend/src/components/SubcategoryForm.tsx

import React, { useState, FormEvent } from 'react';
import { X, Tag } from 'lucide-react';
import { CreateSubcategoryRequest } from '../types/subcategory';

interface Props {
    onSubmit: (data: CreateSubcategoryRequest) => Promise<void>;
    onCancel: () => void;
}

export default function SubcategoryForm({ onSubmit, onCancel }: Props) {
    const [name, setName] = useState('');
    const [categoryType, setCategoryType] = useState<
        'taxable' | 'non-taxable' | 'vat-refundable'
    >('taxable');
    const [error, setError] = useState('');

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            setError('Name is required');
            return;
        }
        if (!categoryType) {
            setError('Category type is required');
            return;
        }
        setError('');
        try {
            await onSubmit({ name: name.trim(), categoryType });
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        }
    };

    const fieldClass = `
        box-border h-11 w-full min-w-0 rounded-[10px]
        border border-slate-200 bg-white px-3.5
        text-[13px] font-medium text-slate-800
        outline-none transition
        placeholder:text-slate-400
        hover:border-slate-300
        focus:border-emerald-400
        focus:ring-2 focus:ring-emerald-100
    `;

    const labelClass = `
        mb-1.5 block
        text-[10px] font-semibold uppercase
        tracking-[0.05em] text-slate-500
    `;

    return (
        <div
            className="
                fixed inset-0 z-[1600]
                flex items-center justify-center
                bg-slate-950/45 px-4 py-6
                backdrop-blur-[2px]
            "
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="subcategory-form-title"
                className="
                    flex max-h-[92vh] w-full max-w-[640px]
                    flex-col overflow-hidden
                    rounded-[18px]
                    border border-slate-200
                    bg-white
                    shadow-[0_24px_70px_rgba(15,23,42,0.22)]
                "
            >
                {/* Header */}
                <div
                    className="
                        flex shrink-0 items-start justify-between gap-4
                        border-b border-slate-100
                        px-6 py-5
                        max-sm:px-4
                    "
                >
                    <div className="min-w-0">
                        <p
                            className="
                                m-0 text-[10px] font-bold uppercase
                                tracking-[0.12em] text-blue-600
                            "
                        >
                            Expense management
                        </p>

                        <h2
                            id="subcategory-form-title"
                            className="
                                mb-0 mt-1
                                text-[20px] font-bold
                                tracking-[-0.02em]
                                text-slate-950
                            "
                        >
                            New Subcategory
                        </h2>

                        <p className="mb-0 mt-1 text-[12px] leading-5 text-slate-500">
                            Create a subcategory and choose its tax classification.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close subcategory form"
                        title="Close"
                        className="
                            inline-flex h-9 w-9 shrink-0 items-center justify-center
                            rounded-[9px]
                            border border-emerald-600
                            bg-emerald-600
                            text-white transition
                            hover:border-emerald-700
                            hover:bg-emerald-700
                            focus:outline-none
                            focus:ring-2
                            focus:ring-emerald-200
                        "
                    >
                        <X size={17} />
                    </button>
                </div>

                {error && (
                    <div
                        className="
                            mx-6 mt-4 rounded-[10px]
                            border border-red-200 bg-red-50
                            px-4 py-3
                            text-[12px] font-medium text-red-700
                            max-sm:mx-4
                        "
                    >
                        {error}
                    </div>
                )}

                <form
                    onSubmit={handleSubmit}
                    className="min-h-0 flex-1 overflow-y-auto"
                >
                    <div
                        className="
                            grid grid-cols-1 gap-4
                            px-6 py-5
                            max-sm:px-4
                        "
                    >
                        {/* Name */}
                        <label className="min-w-0">
                            <span className={labelClass}>Name</span>

                            <div className="relative">
                                <Tag
                                    size={15}
                                    className="
                                        pointer-events-none absolute left-3.5 top-1/2
                                        -translate-y-1/2 text-slate-400
                                    "
                                />

                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    required
                                    placeholder="Enter subcategory name"
                                    className={`${fieldClass} pl-10`}
                                />
                            </div>
                        </label>

                        {/* Type */}
                        <div className="min-w-0">
                            <span className={labelClass}>Type</span>

                            <fieldset className="m-0 border-0 p-0">
                                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                                    <label
                                        className={`
                                            flex cursor-pointer items-center gap-3
                                            rounded-[10px] border px-4 py-3
                                            transition
                                            ${
                                                categoryType === 'taxable'
                                                    ? 'border-emerald-300 bg-emerald-50'
                                                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                                            }
                                        `}
                                    >
                                        <input
                                            type="radio"
                                            name="categoryType"
                                            value="taxable"
                                            checked={categoryType === 'taxable'}
                                            onChange={() => setCategoryType('taxable')}
                                            className="h-4 w-4 accent-emerald-600"
                                        />

                                        <span className="text-[13px] font-semibold text-slate-700">
                                            Taxable
                                        </span>
                                    </label>

                                    <label
                                        className={`
                                            flex cursor-pointer items-center gap-3
                                            rounded-[10px] border px-4 py-3
                                            transition
                                            ${
                                                categoryType === 'non-taxable'
                                                    ? 'border-emerald-300 bg-emerald-50'
                                                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                                            }
                                        `}
                                    >
                                        <input
                                            type="radio"
                                            name="categoryType"
                                            value="non-taxable"
                                            checked={categoryType === 'non-taxable'}
                                            onChange={() => setCategoryType('non-taxable')}
                                            className="h-4 w-4 accent-emerald-600"
                                        />

                                        <span className="text-[13px] font-semibold text-slate-700">
                                            Non-taxable
                                        </span>
                                    </label>

                                    <label
                                        className={`
                                            flex cursor-pointer items-center gap-3
                                            rounded-[10px] border px-4 py-3
                                            transition
                                            ${
                                                categoryType === 'vat-refundable'
                                                    ? 'border-emerald-300 bg-emerald-50'
                                                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                                            }
                                        `}
                                    >
                                        <input
                                            type="radio"
                                            name="categoryType"
                                            value="vat-refundable"
                                            checked={categoryType === 'vat-refundable'}
                                            onChange={() => setCategoryType('vat-refundable')}
                                            className="h-4 w-4 accent-emerald-600"
                                        />

                                        <span className="text-[13px] font-semibold text-slate-700">
                                            VAT-Refundable
                                        </span>
                                    </label>
                                </div>
                            </fieldset>
                        </div>
                    </div>

                    {/* Footer */}
                    <div
                        className="
                            flex items-center justify-end gap-2
                            border-t border-slate-100
                            bg-slate-50/50
                            px-6 py-4
                            max-sm:px-4
                        "
                    >
                        <button
                            type="button"
                            onClick={onCancel}
                            className="
                                inline-flex h-10 items-center justify-center
                                rounded-[9px]
                                border border-emerald-200
                                bg-white px-4
                                text-[12px] font-semibold text-emerald-700
                                transition
                                hover:border-emerald-300
                                hover:bg-emerald-50
                                hover:text-emerald-800
                                focus:outline-none
                                focus:ring-2
                                focus:ring-emerald-200
                            "
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="
                                inline-flex h-10 min-w-[104px]
                                items-center justify-center
                                rounded-[9px]
                                border border-emerald-600
                                bg-emerald-600 px-4
                                text-[12px] font-semibold text-white
                                transition
                                hover:border-emerald-700
                                hover:bg-emerald-700
                                focus:outline-none
                                focus:ring-2
                                focus:ring-emerald-200
                            "
                        >
                            Create
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
